import '@ui5/webcomponents-icons/dist/search';
import '@ui5/webcomponents-icons/dist/sys-help';
import React, { KeyboardEvent, useEffect, useRef } from 'react';
import {
  ComboBox,
  ComboBoxItem,
  DateRangePicker,
  FilterBar,
  FilterGroupItem,
  FlexBox,
  FlexBoxJustifyContent,
  Icon,
  Input,
  InputDomRef,
  Label,
  MultiComboBox,
  MultiComboBoxItem,
  Option,
  Select,
  SuggestionItem,
  Switch,
  SwitchDomRef,
  VariantItem,
  VariantManagement
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents/dist/features/InputSuggestions.js';
import { Loading } from 'components/shared/Loading';
import {
  BaseQueryFilters,
  DistributionStatus,
  IssueType,
  Status,
  TranslationType,
  useGetMsGraphUserQuery
} from 'generated/graphql';
import { useTranslation } from 'react-i18next';
import { compareAlphabetically } from 'utils/sorting';
import { useStyles } from './Filters.jss';
import { useFilters, filtersMinLength } from './useFilters';
import { Controller, ControllerFieldState } from 'react-hook-form';
import { env } from 'config/env';
import { filterAllowedTranslationTypes } from 'utils/filterAllowedTranslationTypes';
import { MsalEmailInputSearch } from 'components/shared/MsalEmailInputSearch';
import { STANDARD_VIEW_ID } from './StandardView';

import BusyIndicatorSize from '@ui5/webcomponents/dist/types/BusyIndicatorSize.js';
import CalendarType from '@ui5/webcomponents-base/dist/types/CalendarType.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

type FiltersProps = {
  onFilter: (filters: BaseQueryFilters, isUserInteraction?: boolean) => void;
  internal: boolean;
  onSearchQueriesFetchChange: (fetching: boolean) => void;
};

const languages = [
  'arSA',
  'bgBG',
  'caES',
  'csCZ',
  'cyGB',
  'daDK',
  'deDE',
  'elGR',
  'enGB',
  'enUS',
  'esCO',
  'esES',
  'esMX',
  'etEE',
  'fiFI',
  'frBE',
  'frCA',
  'frFR',
  'heIL',
  'hiIN',
  'hrHR',
  'huHU',
  'hyAM',
  'idID',
  'itIT',
  'jaJP',
  'kkKZ',
  'koKR',
  'ltLT',
  'lvLV',
  'mkMK',
  'mnMN',
  'msMY',
  'nlBE',
  'nlNL',
  'noNO',
  'orIN',
  'plPL',
  'ptBR',
  'ptPT',
  'roRO',
  'ruRU',
  'shYU',
  'skSK',
  'slSI',
  'sqAL',
  'srRS',
  'srYU',
  'svSE',
  'thTH',
  'trTR',
  'ukUA',
  'viVN',
  'zhCN',
  'zhTW'
];

export const Filters: React.FC<FiltersProps> = (props) => {
  const { t } = useTranslation();
  const [{ data, fetching: fetchingMsGraphUser }] = useGetMsGraphUserQuery();
  const classes = useStyles();
  const searchRef = useRef<InputDomRef>(null);
  const {
    form,
    focus,
    setFocus,
    clearFocus,
    searchFilters,
    projectNames,
    isSearchRequired,
    fetchingSearchQueries,
    selectedVariant,
    filtersVisibility,
    isFilterVisibilityDirty,
    allOptions,
    notSelected,
    setIsSearchRequired,
    onGo,
    onClear,
    onCancel,
    onFiltersDialogOpen,
    onFiltersDialogSave,
    handleSaveAs,
    handleSaveManageViews,
    handleOnSave,
    handleChangeSelect
  } = useFilters({ ...props });
  const { control } = form;
  const getSelectChangeValue = (event: CustomEvent) => event.detail.selectedOption.dataset.value;

  const allowQtImports = env?.REACT_APP_ALLOW_QUERY_TRACKER_IMPORTS === 'true' ? true : false;

  const goOnEnter = (e: KeyboardEvent<HTMLElement>) => e.key == 'Enter' && onGo();

  useEffect(() => {
    setTimeout(() => searchRef.current && searchRef.current.focus());
  }, [selectedVariant]);

  const onSelectComboBoxItem = (event: CustomEvent): string[] => {
    const items: string[] = event.detail.items.map((item: HTMLElement) => {
      const element = item as HTMLElement;
      return element.attributes.getNamedItem('value')?.value;
    });
    return items;
  };

  const languagesSorted = [...languages].sort(compareAlphabetically);
  const implementationStatusSorted = Object.values(DistributionStatus)
    .filter((status) => status !== DistributionStatus.DRAFT)
    .sort((a, b) => compareAlphabetically(t(`DISTRIBUTION-${a}`), t(`DISTRIBUTION-${b}`)));

  const onChangeDistributedFieldParser = (event: CustomEvent) => {
    const { checked } = event.target as SwitchDomRef;
    if (checked !== undefined) {
      return checked;
    }
    return false;
  };

  useEffect(() => {
    props.onSearchQueriesFetchChange(fetchingSearchQueries);
  }, [fetchingSearchQueries]);

  if (fetchingSearchQueries || fetchingMsGraphUser) {
    return <Loading size={BusyIndicatorSize.L}></Loading>;
  }

  const { user: msalUser } = data!;

  const renderVariantItems = () => {
    if (searchFilters && searchFilters.length > 0) {
      const selectedId = selectedVariant?.id;
      return searchFilters.map((searchFilter) => (
        <VariantItem
          key={searchFilter.id}
          isDefault={searchFilter.isDefault}
          favorite={searchFilter.favorite}
          selected={searchFilter.id === selectedId}
          author={`${msalUser?.givenName || msalUser?.userPrincipalName} ${msalUser?.surname}`}
          hideDelete={searchFilter.id === STANDARD_VIEW_ID}
          readOnly={searchFilter.id === STANDARD_VIEW_ID}
          labelReadOnly={searchFilter.id === STANDARD_VIEW_ID}
        >
          {searchFilter.name}
        </VariantItem>
      ));
    }
    return (
      <VariantItem selected={true} hideDelete={true}>
        {t('display-query-filters-no-saved-variant')}
      </VariantItem>
    );
  };

  const renderProjectSuggestionItems = (filteredProject: string) => {
    if (filteredProject.length === 0) return;

    return projectNames.data?.projects
      .filter((project) =>
        project.projectName.toLowerCase().includes(filteredProject.toLocaleLowerCase())
      )
      .sort((a, b) => compareAlphabetically(a.projectName, b.projectName))
      .slice(0, 10)
      .map((searchedProjects) => {
        return (
          <SuggestionItem
            id={searchedProjects.id}
            key={searchedProjects.id}
            text={searchedProjects.projectName}
            data-testid="project-suggestion"
          />
        );
      });
  };

  const getValueState = (name: string, value: string, fieldState: ControllerFieldState) => {
    if (focus === name && value.length < filtersMinLength) {
      return ValueState.Information;
    }

    return fieldState.error ? ValueState.Negative : ValueState.None;
  };

  const getDirtyState = (): boolean => {
    return form.formState.isDirty || isFilterVisibilityDirty;
  };

  return (
    <FilterBar
      key={selectedVariant?.id + '|' + selectedVariant?.name}
      id="filter-bar"
      data-testid="filter-bar"
      className={classes.filterBar}
      data-help-id="filter-bar"
      hideToolbar={false}
      filterBarCollapsed={false}
      showGoOnFB
      showClearOnFB
      hideToggleFiltersButton
      hideFilterConfiguration={false}
      onGo={() => onGo()}
      onClear={() => onClear()}
      onFiltersDialogCancel={() => onCancel()}
      onFiltersDialogSave={onFiltersDialogSave}
      onFiltersDialogOpen={onFiltersDialogOpen}
      onFiltersDialogClose={() => {
        setIsSearchRequired(false);
      }}
      header={
        <VariantManagement
          dirtyState={getDirtyState()}
          dirtyStateText="*"
          key={selectedVariant?.id + '|' + selectedVariant?.name}
          hideShare
          hideApplyAutomatically
          hideManageVariants={searchFilters && searchFilters.length === 0}
          closeOnItemSelect
          onSelect={handleChangeSelect}
          onSaveAs={handleSaveAs}
          onSaveManageViews={handleSaveManageViews}
          data-help-id="variant-manager"
          data-testid="variant-manager"
          onSave={handleOnSave}
        >
          {renderVariantItems()}
        </VariantManagement>
      }
    >
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="search"
        hiddenInFilterBar={!filtersVisibility.search}
        id="text-search-group"
        key="text-search-group"
        label={t('display-query-filters-text-search-label')}
        required={isSearchRequired}
        groupName={t('display-query-filters-group-name-question-information')}
        data-help-id="display-query-filters-text-search-label"
      >
        <>
          <Controller
            name="search"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="text-search-group"
                  icon={<Icon name="search" />}
                  placeholder={t('display-query-filters-text-search-placeholder')}
                  title={t('display-query-filters-text-search-placeholder')}
                  data-testid="text-search-input"
                  onKeyUp={goOnEnter}
                  {...field}
                  ref={searchRef}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="targetLanguage"
        hiddenInFilterBar={!filtersVisibility.targetLanguage}
        id="target-language-group"
        key="target-language-group"
        label={t('display-query-filters-target-language-label')}
        groupName={t('display-query-filters-group-name-question-information')}
        data-help-id="display-query-filters-target-language-label"
      >
        <>
          <Controller
            name="targetLanguage"
            control={control}
            render={({ field: { onChange, ...fields } }) => {
              return (
                <ComboBox
                  className={classes.filterItem}
                  aria-labelledby="target-language-group"
                  onChange={(e) => {
                    onChange(e);
                    onGo();
                  }}
                  {...fields}
                >
                  {[
                    <ComboBoxItem key="all-options" text={allOptions} />,
                    languagesSorted.map((language) => (
                      <ComboBoxItem key={language} text={language} />
                    ))
                  ]}
                </ComboBox>
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="status"
        hiddenInFilterBar={!filtersVisibility.status}
        id="status-group"
        key="status-group"
        label={t('display-query-filters-status-label')}
        groupName={t('display-query-filters-group-name-question-information')}
        data-help-id="display-query-filters-status-label"
      >
        <>
          <Controller
            name="status"
            control={control}
            render={({ field }) => {
              return (
                <MultiComboBox
                  className={classes.filterItem}
                  readonly={false}
                  noValidation={false}
                  aria-labelledby="status-filter-label"
                  data-help-id="status-filter"
                  data-testid="status-filter"
                  selectedValues={field.value}
                  onSelectionChange={(event) => {
                    field.onChange(onSelectComboBoxItem(event));
                    onGo();
                  }}
                >
                  {Object.values(Status)
                    .sort((a, b) => compareAlphabetically(t(a), t(b)))
                    .map(
                      (status) =>
                        status && (
                          <MultiComboBoxItem
                            key={status}
                            text={t(status)}
                            data-testid={status}
                            // @ts-ignore
                            value={status}
                          />
                        )
                    )}
                </MultiComboBox>
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="translationType"
        hiddenInFilterBar={!filtersVisibility.translationType}
        id="translation-type-group"
        key="translation-type-group"
        label={t('display-query-filters-translation-type-label')}
        groupName={t('display-query-filters-group-name-question-information')}
        data-help-id="display-query-filters-translation-type-label"
      >
        <>
          <Controller
            name="translationType"
            control={control}
            render={({ field }) => {
              return (
                <Select
                  className={classes.filterItem}
                  data-testid="translation-type-filter"
                  aria-labelledby="translation-type-group"
                  onChange={(event) => {
                    field.onChange(getSelectChangeValue(event));
                    onGo();
                  }}
                >
                  {[
                    <Option
                      key="not-selected"
                      data-value={notSelected}
                      selected={notSelected === field.value}
                    >
                      {notSelected}
                    </Option>,
                    ...Object.values(TranslationType)
                      .filter(filterAllowedTranslationTypes)
                      .sort((a, b) => compareAlphabetically(t(a), t(b)))
                      .map((translationType) => (
                        <Option
                          key={translationType}
                          data-value={translationType}
                          selected={translationType === field.value}
                          data-testid="translation-type-filter-option"
                        >
                          {t(translationType)}
                        </Option>
                      ))
                  ]}
                </Select>
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="issueType"
        hiddenInFilterBar={!filtersVisibility.issueType}
        id="issue-type-group"
        key="issue-type-group"
        label={t('display-query-filters-issue-type-label')}
        groupName={t('display-query-filters-group-name-question-information')}
        data-help-id="display-query-filters-issue-type-label"
      >
        <>
          <Controller
            name="issueType"
            control={control}
            render={({ field }) => {
              return (
                <MultiComboBox
                  className={classes.filterItem}
                  readonly={false}
                  noValidation={false}
                  aria-labelledby="issue-type-filter-label"
                  data-help-id="issue-type-filter"
                  data-testid="issue-type-filter"
                  selectedValues={field.value}
                  onSelectionChange={(event) => {
                    field.onChange(onSelectComboBoxItem(event));
                    onGo();
                  }}
                >
                  {Object.values(IssueType)
                    .sort((a, b) => compareAlphabetically(t(a), t(b)))
                    .map(
                      (issueType) =>
                        issueType && (
                          <MultiComboBoxItem
                            key={issueType}
                            text={t(issueType)}
                            data-testid={issueType}
                            // @ts-ignore
                            value={issueType}
                          />
                        )
                    )}
                </MultiComboBox>
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="xtmProjectName"
        hiddenInFilterBar={!filtersVisibility.xtmProjectName}
        id="xtm-project-name-group"
        key="xtm-project-name-group"
        label={t('display-query-filters-xtm-project-name-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-xtm-project-name-label"
      >
        <>
          <Controller
            name="xtmProjectName"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="xtm-project-name-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="filename"
        hiddenInFilterBar={!filtersVisibility.filename}
        id="filename-group"
        key="filename-group"
        label={t('display-query-filters-filename-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-filename-label"
      >
        <>
          <Controller
            name="filename"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="filename-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="loio"
        hiddenInFilterBar={!filtersVisibility.loio}
        id="loio-group"
        key="loio-group"
        label={t('display-query-filters-loio-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-loio-label"
      >
        <>
          <Controller
            name="loio"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="loio-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="repository"
        hiddenInFilterBar={!filtersVisibility.repository}
        id="repository-group"
        key="repository-group"
        label={t('display-query-filters-repository-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-repository-label"
      >
        <>
          <Controller
            name="repository"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  data-testid="display-query-filters-repository-field"
                  className={classes.filterItem}
                  aria-labelledby="repository-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="collection"
        hiddenInFilterBar={!filtersVisibility.collection}
        id="collection-group"
        key="collection-group"
        label={t('display-query-filters-collection-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-collection-label"
      >
        <>
          <Controller
            name="collection"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="collection-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="stringId"
        hiddenInFilterBar={!filtersVisibility.stringId}
        id="string-id-group"
        key="string-id-group"
        label={t('display-query-filters-string-id-label')}
        groupName={t('display-query-filters-group-name-technical-information')}
        data-help-id="display-query-filters-string-id-label"
      >
        <>
          <Controller
            name="stringId"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="string-id-group"
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="applicationComponent"
          hiddenInFilterBar={!filtersVisibility.applicationComponent}
          id="application-component-group"
          key="application-component-group"
          label={t('display-query-filters-application-component-label')}
          groupName={t('display-query-filters-group-name-technical-information')}
          data-help-id="display-query-filters-application-component-label"
        >
          <Controller
            name="applicationComponent"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  className={classes.filterItem}
                  aria-labelledby="application-component-group"
                  data-testid="application-component-filter"
                  title={t('display-query-filters-application-component-label')}
                  onChange={field.onChange}
                  onFocus={() => setFocus(field.name)}
                  onBlur={() => clearFocus()}
                  onKeyUp={goOnEnter}
                />
              );
            }}
          />
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="sourceContact"
          hiddenInFilterBar={!filtersVisibility.sourceContact}
          id="source-contact-group"
          key="source-contact-group"
          label={t('display-query-filters-source-contact-label')}
          groupName={t('display-query-filters-group-name-technical-information')}
          data-help-id="display-query-filters-source-contact-label"
        >
          <>
            <Controller
              name="sourceContact"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="source-contact-group"
                    placeholder={t('display-query-filters-source-contact-placeholder')}
                    title={t('display-query-filters-source-contact-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {allowQtImports && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="queryTrackerId"
          hiddenInFilterBar={!filtersVisibility.queryTrackerId}
          id="query-tracker-id-group"
          key="query-tracker-id-group"
          label={t('display-query-filters-query-tracker-id-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-query-tracker-id-label"
        >
          <>
            <Controller
              name="queryTrackerId"
              control={control}
              render={({ field }) => {
                return (
                  <Input
                    placeholder={t('display-query-filters-query-tracker-id-placeholder')}
                    aria-labelledby="query-tracker-id-group"
                    onKeyUp={goOnEnter}
                    {...field}
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="project"
        hiddenInFilterBar={!filtersVisibility.project}
        id="project-group"
        key="project-group"
        label={t('display-query-filters-projects-label')}
        groupName={t('display-query-filters-group-name-administrative-details')}
        data-help-id="display-query-filters-projects-label"
      >
        <>
          <Controller
            name="project"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  showSuggestions
                  aria-labelledby="project-group"
                  placeholder={t('display-query-filters-project-placeholder')}
                  value={field.value}
                  onInput={field.onChange}
                  onSelectionChange={(e) => {
                    const text = (e.detail.item as HTMLElement & { text?: string })?.text;
                    if (text !== undefined) field.onChange(text);
                  }}
                  onKeyUp={goOnEnter}
                >
                  {renderProjectSuggestionItems(field.value)}
                </Input>
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="createdBy"
        hiddenInFilterBar={!filtersVisibility.createdBy}
        id="created-by-group"
        key="created-by-group"
        label={t('display-query-filters-created-by-label')}
        groupName={t('display-query-filters-group-name-administrative-details')}
        data-help-id="display-query-filters-created-by-label"
      >
        <>
          <Controller
            name="createdBy"
            control={control}
            render={({ field: { onChange, ...fields }, fieldState }) => {
              return (
                <MsalEmailInputSearch
                  {...fields}
                  aria-labelledby="created-by-group"
                  placeholder={
                    props.internal
                      ? t('display-query-filters-created-by-placeholder')
                      : t('display-query-filters-created-by-placeholder-external')
                  }
                  title={t('display-query-filters-created-by-placeholder')}
                  className={classes.filterItem}
                  onEmailChange={onChange}
                  customSuggestionItemSelector={(item) => item.sapNumber || item.userPrincipalName!}
                  inputMode="email"
                  onFocus={() => setFocus(fields.name)}
                  onBlur={() => clearFocus()}
                  onKeyUp={goOnEnter}
                  showClearIcon
                  valueState={getValueState(fields.name, fields.value, fieldState)}
                  valueStateMessage={
                    <Label wrappingType={WrappingType.Normal}>
                      {t('display-value-state-message-field-min-length', {
                        length: filtersMinLength
                      })}
                    </Label>
                  }
                  icon={
                    <Icon
                      className={classes.helpCursor}
                      name="sys-help"
                      title={t('display-value-state-message-field-generic-min-length', {
                        length: filtersMinLength
                      })}
                    />
                  }
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="submitterGroupName"
        hiddenInFilterBar={!filtersVisibility.submitterGroupName}
        id="submitter-group-name-group"
        key="submitter-group-name-group"
        label={t('display-query-filters-submitter-group-name-label')}
        data-help-id="display-query-filters-submitter-group-name-label"
        groupName={t('display-query-filters-group-name-administrative-details')}
      >
        <>
          <Controller
            name="submitterGroupName"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  className={classes.filterItem}
                  aria-labelledby="submitter-group-name-group"
                  placeholder={t('display-query-filters-submitter-by-placeholder')}
                  onKeyUp={goOnEnter}
                  {...field}
                />
              );
            }}
          />
        </>
      </FilterGroupItem>
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="qualityExpert"
          hiddenInFilterBar={!filtersVisibility.qualityExpert}
          id="quality-expert-group"
          key="quality-expert-group"
          label={t('display-query-filters-quality-engineer-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-quality-engineer-label"
        >
          <>
            <Controller
              name="qualityExpert"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="quality-expert-group"
                    placeholder={t('display-query-filters-quality-engineer-placeholder')}
                    title={t('display-query-filters-quality-engineer-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="projectManager"
          hiddenInFilterBar={!filtersVisibility.projectManager}
          id="project-manager-group"
          key="project-manager-group"
          label={t('display-query-filters-project-manager-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-project-manager-label"
        >
          <>
            <Controller
              name="projectManager"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="project-manager-group"
                    placeholder={t('display-query-filters-project-manager-placeholder')}
                    title={t('display-query-filters-project-manager-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="queryResponsible"
          hiddenInFilterBar={!filtersVisibility.queryResponsible}
          id="query-responsible-group"
          key="query-responsible-group"
          label={t('display-query-filters-query-responsible-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-query-responsible-label"
        >
          <>
            <Controller
              name="queryResponsible"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="query-responsible-group"
                    placeholder={t('display-query-filters-query-responsible-placeholder')}
                    title={t('display-query-filters-query-responsible-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="assignee"
          hiddenInFilterBar={!filtersVisibility.assignee}
          id="assignee-group"
          key="assignee-group"
          label={t('display-query-filters-assignee-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-assignee-label"
        >
          <>
            <Controller
              name="assignee"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="assignee-group"
                    title={t('display-query-filters-assignee-placeholder')}
                    placeholder={t('display-query-filters-assignee-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="answerer"
          hiddenInFilterBar={!filtersVisibility.answerer}
          id="answerer-group"
          key="answerer-group"
          label={t('display-query-filters-answerer-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-answerer-label"
        >
          <>
            <Controller
              name="answerer"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="answerer-group"
                    title={t('display-query-filters-answerer-placeholder')}
                    placeholder={t('display-query-filters-answerer-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="substitutePm"
          hiddenInFilterBar={!filtersVisibility.substitutePm}
          id="substitute-pm-group"
          key="substitute-pm-group"
          label={t('display-query-filters-substitute-pm-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-substitute-pm-label"
        >
          <>
            <Controller
              name="substitutePm"
              control={control}
              render={({ field: { onChange, ...fields }, fieldState }) => {
                return (
                  <MsalEmailInputSearch
                    {...fields}
                    aria-labelledby="substitute-pm-group"
                    placeholder={t('display-query-filters-substitute-pm-placeholder')}
                    title={t('display-query-filters-substitute-pm-placeholder')}
                    className={classes.filterItem}
                    onEmailChange={onChange}
                    customSuggestionItemSelector={(item) =>
                      item.sapNumber || item.userPrincipalName!
                    }
                    inputMode="email"
                    onFocus={() => setFocus(fields.name)}
                    onBlur={() => clearFocus()}
                    onKeyUp={goOnEnter}
                    showClearIcon
                    valueState={getValueState(fields.name, fields.value, fieldState)}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-field-min-length', {
                          length: filtersMinLength
                        })}
                      </Label>
                    }
                    icon={
                      <Icon
                        className={classes.helpCursor}
                        name="sys-help"
                        title={t('display-value-state-message-field-generic-min-length', {
                          length: filtersMinLength
                        })}
                      />
                    }
                  />
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      {!props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="distributed"
          hiddenInFilterBar={!filtersVisibility.distributed}
          id="distributed-group"
          key="distributed-group"
          label={t('display-query-filters-distributed-label')}
          groupName={t('display-query-filters-group-name-question-information')}
          data-help-id="display-query-filters-distributed-label"
          style={{ justifyContent: 'center', display: 'flex' }}
        >
          <FlexBox justifyContent={FlexBoxJustifyContent.Center}>
            <Controller
              name="distributed"
              control={control}
              render={({ field }) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { value: _switchValue, ...switchFieldProps } = field;
                return (
                  <Switch
                    {...switchFieldProps}
                    textOff="NO"
                    textOn="YES"
                    onChange={(event) => {
                      field.onChange(onChangeDistributedFieldParser(event));
                      onGo();
                    }}
                    checked={field.value}
                    data-testid="switch-filter"
                  />
                );
              }}
            />
          </FlexBox>
        </FilterGroupItem>
      )}
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="createdAt"
        hiddenInFilterBar={!filtersVisibility.createdAt}
        id="createdAt-group"
        key="createdAt-group"
        label={t('display-query-filters-createdAt-label')}
        groupName={t('display-query-filters-group-name-administrative-details')}
        data-help-id="display-query-filters-createdAt-label"
      >
        <Controller
          name="createdAt"
          control={control}
          render={({ field }) => {
            return (
              <DateRangePicker
                className={classes.filterItem}
                {...field}
                value={field.value || ''}
                onChange={(event) => {
                  if (event.detail.valid) {
                    field.onChange(event.detail.value);
                    onGo();
                  }
                }}
                primaryCalendarType={CalendarType.Gregorian}
                data-testid="created-at-filter"
                aria-labelledby="createdAt-group"
              />
            );
          }}
        />
      </FilterGroupItem>
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="account"
          hiddenInFilterBar={!filtersVisibility.account}
          id="account-group"
          key="account-group"
          label={t('display-query-filters-account-label')}
          groupName={t('display-query-filters-group-name-administrative-details')}
          data-help-id="display-query-filters-account-label"
        >
          <Controller
            name="account"
            control={control}
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  className={classes.filterItem}
                  aria-labelledby="account-group"
                  data-testid="account-filter"
                  title={t('display-query-filters-account-label')}
                  onChange={field.onChange}
                  onFocus={() => setFocus(field.name)}
                  onBlur={() => clearFocus()}
                  onKeyUp={goOnEnter}
                />
              );
            }}
          />
        </FilterGroupItem>
      )}
      {props.internal && (
        <FilterGroupItem
          className={classes.filterGroupItem}
          filterKey="implementationStatus"
          hiddenInFilterBar={!filtersVisibility.implementationStatus}
          id="implementation-status-group"
          key="implementation-status-group"
          label={t('display-query-filters-implementation-status-label')}
          groupName={t('display-query-filters-group-name-distribution')}
          data-help-id="display-query-filters-implementation-status-label"
        >
          <>
            <Controller
              name="implementationStatus"
              control={control}
              render={({ field }) => {
                return (
                  <MultiComboBox
                    className={classes.filterItem}
                    readonly={false}
                    noValidation={false}
                    aria-labelledby="implementation-status-filter-label"
                    data-help-id="implementation-status-filter"
                    data-testid="implementation-status-filter"
                    selectedValues={field.value}
                    onSelectionChange={(event) => {
                      field.onChange(onSelectComboBoxItem(event));
                      onGo();
                    }}
                  >
                    {implementationStatusSorted.map(
                      (implementationStatus) =>
                        implementationStatus && (
                          <MultiComboBoxItem
                            key={implementationStatus}
                            text={t(`DISTRIBUTION-${implementationStatus}`)}
                            data-testid={implementationStatus}
                            // @ts-ignore
                            value={implementationStatus}
                          />
                        )
                    )}
                  </MultiComboBox>
                );
              }}
            />
          </>
        </FilterGroupItem>
      )}
      <FilterGroupItem
        className={classes.filterGroupItem}
        filterKey="implementationLanguage"
        hiddenInFilterBar={!filtersVisibility.implementationLanguage}
        id="implementation-language-group"
        key="implementation-language-group"
        label={t('display-query-filters-implementation-language-label')}
        groupName={t('display-query-filters-group-name-distribution')}
        data-help-id="display-query-filters-implementation-language-label"
      >
        <>
          <Controller
            name="implementationLanguage"
            control={control}
            render={({ field }) => {
              return (
                <MultiComboBox
                  className={classes.filterItem}
                  readonly={false}
                  noValidation={false}
                  aria-labelledby="implementation-language-filter-label"
                  data-help-id="implementation-language-filter"
                  data-testid="implementation-language-filter"
                  selectedValues={field.value}
                  onSelectionChange={(event) => {
                    field.onChange(onSelectComboBoxItem(event));
                    onGo();
                  }}
                >
                  {[
                    <MultiComboBoxItem key="all-options" text={allOptions} />,
                    ...languages.map((language) => (
                      <MultiComboBoxItem
                        key={language}
                        text={language}
                        data-testid={language}
                        // @ts-ignore
                        value={language}
                      />
                    ))
                  ]}
                </MultiComboBox>
              );
            }}
          />
        </>
      </FilterGroupItem>
    </FilterBar>
  );
};
